import { useCallback, useEffect, useMemo, useState } from "react";
import { EMPTY_FILTERS, resolveDateRange, formatDisplayDate, normalizeCommissionView } from "./commissionsConstants";

const AUTO_APPLY_FILTERS_MS = 400;

/** Filter fields that drive the applications list (excludes tab, sort, report). */
export function getCommissionsFilterQueryState(filters = {}) {
  return {
    type: filters.type,
    providerId: filters.providerId,
    courseId: filters.courseId,
    intakeId: filters.intakeId,
    location: filters.location,
    processingAgentId: filters.processingAgentId,
    referringAgentId: filters.referringAgentId,
    processingBranchId: filters.processingBranchId,
    referringBranchId: filters.referringBranchId,
    statuses: filters.statuses,
    clientUserId: filters.clientUserId,
    clientDob: filters.clientDob,
    dateRange: filters.dateRange,
  };
}

function normalizeDraftToApplied(draft) {
  return {
    ...draft,
    report: draft.type === "migration" ? "general" : draft.report,
  };
}

function applyChipRemovalToFilters(prev, chip) {
  const next = { ...prev };
  if (chip.type === "provider") {
    next.providerId = null;
    next.courseId = null;
  } else if (chip.type === "course") {
    next.courseId = null;
  } else if (chip.type === "intake") {
    next.intakeId = null;
  } else if (chip.type === "location") {
    next.location = null;
  } else if (chip.type === "processingAgent") {
    next.processingAgentId = null;
  } else if (chip.type === "referringAgent") {
    next.referringAgentId = null;
  } else if (chip.type === "processingBranch") {
    next.processingBranchId = null;
  } else if (chip.type === "referringBranch") {
    next.referringBranchId = null;
  } else if (chip.type === "status") {
    next.statuses = (next.statuses || []).filter((s) => s.id !== chip.id);
  } else if (chip.type === "dateRange") {
    next.dateRange = { preset: "all", startDate: "", endDate: "" };
  } else if (chip.type === "clientUserId") {
    next.clientUserId = "";
  } else if (chip.type === "clientDob") {
    next.clientDob = "";
  } else if (chip.type === "search") {
    next.searchTerm = "";
  }
  return next;
}

export function countFilterSelections(filters) {
  let count = 0;
  if (filters.providerId) count += 1;
  if (filters.courseId) count += 1;
  if (filters.intakeId) count += 1;
  if (filters.location) count += 1;
  if (filters.processingAgentId) count += 1;
  if (filters.referringAgentId) count += 1;
  if (filters.processingBranchId) count += 1;
  if (filters.referringBranchId) count += 1;
  if (filters.statuses?.length) count += 1;
  if (filters.clientUserId?.trim()) count += 1;
  if (filters.clientDob?.trim()) count += 1;
  const { startDate, endDate } = resolveDateRange(filters.dateRange);
  if (startDate || endDate) count += 1;
  return count;
}

export function countActiveFilters(filters) {
  let count = countFilterSelections(filters);
  if (filters.searchTerm?.trim()) count += 1;
  return count;
}

export function buildActiveChips(
  filters,
  providersList = [],
  coursesList = [],
  intakes = [],
  processingAgentsList = [],
  referringAgentsList = [],
  processingBranchesList = [],
  referringBranchesList = [],
) {
  const chips = [];

  if (filters.providerId) {
    const name =
      providersList.find((x) => String(x.id) === String(filters.providerId))?.name ||
      `Provider ${filters.providerId}`;
    chips.push({ id: `provider-${filters.providerId}`, type: "provider", label: `Provider: ${name}` });
  }

  if (filters.courseId) {
    const name =
      coursesList.find((x) => String(x.id) === String(filters.courseId))?.name ||
      `Course ${filters.courseId}`;
    chips.push({ id: `course-${filters.courseId}`, type: "course", label: `Course: ${name}` });
  }

  if (filters.intakeId) {
    const intake = intakes.find((i) => String(i.id) === String(filters.intakeId));
    chips.push({
      id: `intake-${filters.intakeId}`,
      type: "intake",
      label: intake?.title || `Intake ${filters.intakeId}`,
    });
  }

  if (filters.location) {
    const label =
      filters.location === "onshore"
        ? "Onshore"
        : filters.location === "offshore"
          ? "Offshore"
          : filters.location;
    chips.push({
      id: `location-${filters.location}`,
      type: "location",
      label: `Location: ${label}`,
    });
  }

  if (filters.processingAgentId) {
    const name =
      processingAgentsList.find(
        (x) => String(x.id) === String(filters.processingAgentId),
      )?.name || `Agent ${filters.processingAgentId}`;
    chips.push({
      id: `processing-agent-${filters.processingAgentId}`,
      type: "processingAgent",
      label: `Processing Agent: ${name}`,
    });
  }

  if (filters.referringAgentId) {
    const name =
      referringAgentsList.find(
        (x) => String(x.id) === String(filters.referringAgentId),
      )?.name || `Agent ${filters.referringAgentId}`;
    chips.push({
      id: `referring-agent-${filters.referringAgentId}`,
      type: "referringAgent",
      label: `Referring Agent: ${name}`,
    });
  }

  if (filters.processingBranchId) {
    const name =
      processingBranchesList.find(
        (x) => String(x.id) === String(filters.processingBranchId),
      )?.name || `Branch ${filters.processingBranchId}`;
    chips.push({
      id: `processing-branch-${filters.processingBranchId}`,
      type: "processingBranch",
      label: `Processing Branch: ${name}`,
    });
  }

  if (filters.referringBranchId) {
    const name =
      referringBranchesList.find(
        (x) => String(x.id) === String(filters.referringBranchId),
      )?.name || `Branch ${filters.referringBranchId}`;
    chips.push({
      id: `referring-branch-${filters.referringBranchId}`,
      type: "referringBranch",
      label: `Referring Branch: ${name}`,
    });
  }

  if (filters.statuses?.length) {
    filters.statuses.forEach((status) => {
      chips.push({
        id: status.id,
        type: "status",
        label: `Status: ${status.title}`,
      });
    });
  }

  if (filters.clientUserId?.trim()) {
    chips.push({
      id: "client-user-id",
      type: "clientUserId",
      label: `Client / User ID: ${filters.clientUserId.trim()}`,
    });
  }

  if (filters.clientDob?.trim()) {
    chips.push({
      id: "client-dob",
      type: "clientDob",
      label: `DOB: ${formatDisplayDate(filters.clientDob)}`,
    });
  }

  const { startDate, endDate } = resolveDateRange(filters.dateRange);
  if (startDate || endDate) {
    const dateLabel =
      startDate && endDate
        ? `Date: ${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`
        : `Date: ${formatDisplayDate(startDate || endDate)}`;
    chips.push({
      id: "date-range",
      type: "dateRange",
      label: dateLabel,
    });
  }

  if (filters.searchTerm?.trim()) {
    chips.push({ id: "search", type: "search", label: `Search: ${filters.searchTerm.trim()}` });
  }

  return chips;
}

export function useCommissionsFilters() {
  const [draft, setDraft] = useState({ ...EMPTY_FILTERS });
  const [applied, setApplied] = useState({ ...EMPTY_FILTERS });
  const [filtersOpen, setFiltersOpen] = useState(true);

  const applyFilters = useCallback(() => {
    const nextApplied = normalizeDraftToApplied(draft);
    setApplied(nextApplied);
    setDraft(nextApplied);
  }, [draft]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextApplied = normalizeDraftToApplied(draft);
      setApplied((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(nextApplied)) {
          return prev;
        }
        return nextApplied;
      });
    }, AUTO_APPLY_FILTERS_MS);

    return () => window.clearTimeout(timer);
  }, [draft]);

  const clearFilters = useCallback(() => {
    setDraft((prev) => ({
      ...EMPTY_FILTERS,
      type: prev.type,
      report: prev.type === "migration" ? "general" : prev.report,
      commissionView: prev.commissionView,
      sortBy: prev.sortBy,
      sortDir: prev.sortDir,
    }));
    setApplied((prev) => ({
      ...EMPTY_FILTERS,
      type: prev.type,
      report: prev.type === "migration" ? "general" : prev.report,
      commissionView: prev.commissionView,
      sortBy: prev.sortBy,
      sortDir: prev.sortDir,
    }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraft({ ...applied });
  }, [applied]);

  const setDraftField = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setReport = useCallback((report) => {
    setDraft((prev) => ({ ...prev, report }));
    setApplied((prev) => ({ ...prev, report }));
  }, []);

  const setCommissionView = useCallback((commissionView) => {
    const normalized = normalizeCommissionView(commissionView);
    setDraft((prev) => ({ ...prev, commissionView: normalized }));
    setApplied((prev) => ({ ...prev, commissionView: normalized }));
  }, []);

  const setSort = useCallback((sortBy, sortDir) => {
    setDraft((prev) => ({ ...prev, sortBy, sortDir }));
    setApplied((prev) => ({ ...prev, sortBy, sortDir }));
  }, []);

  const toggleSort = useCallback((columnKey, direction) => {
    setDraft((prev) => {
      let sortBy = columnKey;
      let sortDir = direction || "desc";
      if (!direction) {
        if (prev.sortBy === columnKey) {
          sortDir = prev.sortDir === "desc" ? "asc" : "desc";
        } else {
          sortDir = "desc";
        }
      }
      setApplied((a) => ({ ...a, sortBy, sortDir }));
      return { ...prev, sortBy, sortDir };
    });
  }, []);

  const removeChip = useCallback((chip) => {
    setDraft((prev) => {
      const next = applyChipRemovalToFilters(prev, chip);
      const nextApplied = normalizeDraftToApplied(next);
      setApplied((current) =>
        JSON.stringify(current) === JSON.stringify(nextApplied)
          ? current
          : nextApplied,
      );
      return next;
    });
  }, []);

  /** Apply provider filter immediately (syncs draft + applied for list fetch). */
  const setProviderFilter = useCallback((providerId) => {
    setDraft((prev) => {
      const next = {
        ...prev,
        providerId: providerId || null,
        courseId: null,
      };
      const nextApplied = normalizeDraftToApplied(next);
      setApplied(nextApplied);
      return next;
    });
  }, []);

  const hasPendingChanges = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(applied);
  }, [draft, applied]);

  return {
    draft,
    applied,
    filtersOpen,
    setFiltersOpen,
    setDraft,
    setDraftField,
    applyFilters,
    clearFilters,
    resetDraft,
    setReport,
    setCommissionView,
    setSort,
    toggleSort,
    removeChip,
    setProviderFilter,
    hasPendingChanges,
  };
}
